import React from 'react';
import AirTimeDisplay from '../AirTimeDisplay';

// Safe number formatting helper
const safeFixed = (value, decimals = 2) => {
  return (value != null && !isNaN(value)) ? Number(value).toFixed(decimals) : 'N/A';
};

const getLegMeasurement = (jumpMetrics) => {
  const legPosition = jumpMetrics?.position_analysis?.leg_position;

  if (legPosition?.tightness_score !== undefined) {
    return {
      score: legPosition.tightness_score,
      climbTimeS: legPosition.climb_time_s,
    };
  }

  if (legPosition?.leg_wrap_best !== undefined) {
    return {
      score: legPosition.leg_wrap_best,
      climbTimeS: undefined,
    };
  }

  if (jumpMetrics?.air_leg_wrap_best !== undefined) {
    return {
      score: jumpMetrics.air_leg_wrap_best,
      climbTimeS: undefined,
    };
  }

  if (jumpMetrics?.air_leg_wrap_avg !== undefined) {
    return {
      score: jumpMetrics.air_leg_wrap_avg,
      climbTimeS: undefined,
    };
  }

  return null;
};

const getArmMeasurement = (jumpMetrics) => {
  const armPosition = jumpMetrics?.position_analysis?.arm_position;
  const hasArmFields = Boolean(
    armPosition &&
    (
      Object.prototype.hasOwnProperty.call(armPosition, 'arm_closure_ratio') ||
      Object.prototype.hasOwnProperty.call(armPosition, 'min_arm_closure_ratio')
    )
  ) || Object.prototype.hasOwnProperty.call(jumpMetrics || {}, 'air_arm_closure_avg') ||
       Object.prototype.hasOwnProperty.call(jumpMetrics || {}, 'air_arm_closure_best');

  if (!hasArmFields) {
    return null;
  }

  const sustainedRatio = armPosition?.arm_closure_ratio ?? jumpMetrics?.air_arm_closure_avg;
  const peakRatio = armPosition?.min_arm_closure_ratio ?? jumpMetrics?.air_arm_closure_best;
  const pullInTimeS = armPosition?.pull_in_time_s;

  return {
    sustainedRatio,
    peakRatio,
    pullInTimeS,
    unavailable: sustainedRatio == null && peakRatio == null,
  };
};

const KeyMeasurementsDisplay = ({ analysisResult }) => {
  const biomechData = analysisResult?.biomechanical_data;
  const jumpMetrics = biomechData?.jump_metrics;
  const spinMetrics = biomechData?.spin_metrics;

  if (!biomechData || (!jumpMetrics && !spinMetrics)) {
    return <p className="no-measurements">No measurement data available</p>;
  }

  return (
    <div className="measurements-grid">
      {/* COMBINATION JUMP METRICS */}
      {jumpMetrics?.combination_type === 'jump_combination' && (
        <CombinationJumpMetrics jumpMetrics={jumpMetrics} />
      )}

      {/* SPIN METRICS */}
      {spinMetrics && <SpinMetrics spinMetrics={spinMetrics} />}

      {/* SINGLE JUMP METRICS */}
      {jumpMetrics && jumpMetrics.combination_type !== 'jump_combination' && (
        <SingleJumpMetrics jumpMetrics={jumpMetrics} />
      )}
    </div>
  );
};

// Combination Jump Metrics Sub-component
const CombinationJumpMetrics = ({ jumpMetrics }) => (
  <>
    {/* ISU Score */}
    {jumpMetrics.isu_evaluation && (
      <>
        <div className="measurement-item highlight combo-header" style={{ gridColumn: '1 / -1' }}>
          <span className="measurement-label">ISU Combination Code:</span>
          <span className="measurement-value">
            {jumpMetrics.isu_evaluation.isu_code}
          </span>
          <span className="measurement-subtext">
            Base: {jumpMetrics.isu_evaluation.base_value} |
            GOE: {jumpMetrics.isu_evaluation.estimated_goe > 0 ? '+' : ''}
            {jumpMetrics.isu_evaluation.estimated_goe} |
            Score: {safeFixed(jumpMetrics.isu_evaluation.estimated_score, 2)}
          </span>
        </div>

        {/* Combination Valid Status */}
        <div className={`measurement-item ${jumpMetrics.isu_evaluation.is_valid_combination ? 'highlight' : ''}`}>
          <span className="measurement-label">Combination Status:</span>
          <span className="measurement-value">
            {jumpMetrics.isu_evaluation.is_valid_combination ? '✓ Valid' : '✗ Invalid'}
          </span>
          <span className="measurement-subtext">
            {jumpMetrics.isu_evaluation.is_valid_combination ?
              '(ISU requirements met)' : '(Transition too long)'}
          </span>
        </div>

        {/* Technical Calls */}
        {jumpMetrics.isu_evaluation.technical_calls?.length > 0 && (
          <div className="measurement-item" style={{ gridColumn: '1 / -1' }}>
            <span className="measurement-label">Technical Calls:</span>
            <span className="measurement-value">
              {jumpMetrics.isu_evaluation.technical_calls.join(', ')}
            </span>
          </div>
        )}
      </>
    )}

    {/* Individual Jumps */}
    {jumpMetrics.individual_jumps?.map((jump, idx) => (
      <React.Fragment key={idx}>
        <div className="measurement-item highlight" style={{ gridColumn: '1 / -1', marginTop: idx > 0 ? '8px' : '0' }}>
          <span className="measurement-label">Jump {jump.jump_number}: {jump.expected_type?.replace('jump_', '').replace(/_/g, ' ').toUpperCase()}</span>
        </div>

        {jump.rotation_total_degrees !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Rotations(Air):</span>
            <span className="measurement-value">
              {safeFixed(jump.rotation_total_degrees, 0)}°
            </span>
          </div>
        )}

        {jump.max_vertical_displacement_cm !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Height:</span>
            <span className="measurement-value">{safeFixed(jump.max_vertical_displacement_cm, 1)} cm</span>
          </div>
        )}

        {jump.rotation_speed_avg_rev_s !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Rotation Speed:</span>
            <span className="measurement-value">{safeFixed(jump.rotation_speed_avg_rev_s, 2)} rev/s</span>
          </div>
        )}

        {jump.air_time !== undefined && (
          <AirTimeDisplay metrics={jump} showDetails={true} />
        )}

        {jump.takeoff_velocity_peak_m_s !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Takeoff Speed:</span>
            <span className="measurement-value">{safeFixed(jump.takeoff_velocity_peak_m_s, 2)} m/s</span>
          </div>
        )}

        {jump.landing_speed_m_s !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Landing Speed:</span>
            <span className="measurement-value">{safeFixed(jump.landing_speed_m_s, 2)} m/s</span>
          </div>
        )}
      </React.Fragment>
    ))}

    {/* Transitions */}
    {jumpMetrics.transitions?.map((transition, idx) => (
      <React.Fragment key={`transition-${idx}`}>
        <div className="measurement-item highlight" style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
          <span className="measurement-label">
            Transition {transition.from_jump} → {transition.to_jump}
          </span>
        </div>

        <div className="measurement-item">
          <span className="measurement-label">Duration:</span>
          <span className="measurement-value">
            {transition.duration != null && transition.duration > 0
              ? safeFixed(transition.duration, 3) + ' s'
              : 'Undetected'}
          </span>
          <span className="measurement-subtext">({transition.quality || 'N/A'})</span>
        </div>

        {transition.average_horizontal_speed_m_s !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Speed:</span>
            <span className="measurement-value">{safeFixed(transition.average_horizontal_speed_m_s, 2)} m/s</span>
          </div>
        )}

        {transition.average_height_cm !== undefined && (
          <div className="measurement-item">
            <span className="measurement-label">Avg Height:</span>
            <span className="measurement-value">{safeFixed(transition.average_height_cm, 1)} cm</span>
          </div>
        )}
      </React.Fragment>
    ))}

    {/* Overall Quality */}
    {jumpMetrics.overall_quality && (
      <div className="measurement-item quality-badge" style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
        <span className="measurement-label">Overall Quality:</span>
        <span className={`quality-level ${jumpMetrics.overall_quality.overall_rating}`}>
          {jumpMetrics.overall_quality.overall_rating.replace(/_/g, ' ')}
        </span>
        <span className="measurement-subtext" style={{ display: 'block', marginTop: '4px' }}>
          {jumpMetrics.overall_quality.message}
        </span>
      </div>
    )}
  </>
);

// Spin Metrics Sub-component
const SpinMetrics = ({ spinMetrics }) => (
  <>
    {spinMetrics.average_rotation_speed_rev_s !== undefined && (
      <div className="measurement-item highlight">
        <span className="measurement-label">Avg Rotation Speed:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.average_rotation_speed_rev_s, 2)} rev/s
        </span>
        <span className="measurement-subtext">
          ({safeFixed(spinMetrics.average_rotation_speed, 0)} deg/s)
        </span>
      </div>
    )}

    {spinMetrics.peak_rotation_speed_rev_s !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Peak Speed:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.peak_rotation_speed_rev_s, 2)} rev/s
        </span>
        <span className="measurement-subtext">
          ({safeFixed(spinMetrics.peak_rotation_speed, 0)} deg/s)
        </span>
      </div>
    )}

    {spinMetrics.travel_distance_cm !== undefined && (
      <div className="measurement-item highlight">
        <span className="measurement-label">Travel Distance:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.travel_distance_cm, 1)} cm
        </span>
        <span className="measurement-subtext">
          {spinMetrics.travel_distance_cm < 15 ? '(Elite <15cm ✓)' :
            spinMetrics.travel_distance_cm < 25 ? '(Good <25cm)' :
              '(Traveling >25cm ⚠)'}
        </span>
      </div>
    )}

    {spinMetrics.speed_increase_detected !== undefined && (
      <div className="measurement-item highlight">
        <span className="measurement-label">Speed Increase:</span>
        <span className="measurement-value">
          {spinMetrics.speed_increase_detected ? 'YES ✓' : 'NO'}
        </span>
        {spinMetrics.speed_increase_detected && spinMetrics.max_speed_increase_percent !== undefined && (
          <span className="measurement-subtext">
            (+{safeFixed(spinMetrics.max_speed_increase_percent, 1)}% - ISU Level Feature)
          </span>
        )}
      </div>
    )}

    {spinMetrics.rotation_direction && (
      <div className="measurement-item">
        <span className="measurement-label">Direction:</span>
        <span className="measurement-value">
          {spinMetrics.rotation_direction === 'CW' ? 'Clockwise (CW)' :
            spinMetrics.rotation_direction === 'CCW' ? 'Counter-CW (CCW)' :
              'Unknown'}
        </span>
      </div>
    )}

    {spinMetrics.centering_quality_score !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Centering Quality:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.centering_quality_score * 100, 0)}%
        </span>
        <span className="measurement-subtext">
          {spinMetrics.is_traveling ? '(Traveling ⚠)' : '(Centered ✓)'}
        </span>
      </div>
    )}

    {spinMetrics.entry_quality !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Entry Quality:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.entry_quality * 100, 0)}%
        </span>
        <span className="measurement-subtext">(First 3 rotations)</span>
      </div>
    )}

    {spinMetrics.exit_quality !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Exit Quality:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.exit_quality * 100, 0)}%
        </span>
        <span className="measurement-subtext">(Last 3 rotations)</span>
      </div>
    )}

    {spinMetrics.rotation_consistency !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Consistency:</span>
        <span className="measurement-value">
          {safeFixed(spinMetrics.rotation_consistency * 100, 0)}%
        </span>
        <span className="measurement-subtext">(Speed stability)</span>
      </div>
    )}
  </>
);

// Single Jump Metrics Sub-component
const SingleJumpMetrics = ({ jumpMetrics }) => {
  const legMeasurement = getLegMeasurement(jumpMetrics);
  const armMeasurement = getArmMeasurement(jumpMetrics);

  return (
    <>
    {jumpMetrics.rotation_total_degrees !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Rotations(Air):</span>
        <span className="measurement-value">
          {jumpMetrics.rotation_total_degrees.toFixed(0)}°
        </span>
      </div>
    )}

    {jumpMetrics.rotation_speed_avg_rev_s !== undefined && (
      <div className="measurement-item highlight">
        <span className="measurement-label">Rotation Speed:</span>
        <span className="measurement-value">
          {jumpMetrics.rotation_speed_avg_rev_s.toFixed(2)} rev/s
        </span>
        <span className="measurement-subtext">
          (peak: {jumpMetrics.rotation_speed_peak_rev_s?.toFixed(2) || 'N/A'} rev/s)
        </span>
      </div>
    )}

    {jumpMetrics.max_vertical_displacement_cm !== undefined && (
      <div className="measurement-item highlight">
        <span className="measurement-label">Jump Height:</span>
        <span className="measurement-value">
          {jumpMetrics.max_vertical_displacement_cm.toFixed(1)} cm
        </span>
      </div>
    )}

    {jumpMetrics.horizontal_distance_cm !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Jump Distance:</span>
        <span className="measurement-value">
          {jumpMetrics.horizontal_distance_cm.toFixed(1)} cm
        </span>
      </div>
    )}

    {/* Arm Tightness */}
    {armMeasurement?.unavailable ? (
      <div className="measurement-item">
        <span className="measurement-label">Arm Tightness (Sustained):</span>
        <span className="measurement-value">Unavailable</span>
        <span className="measurement-subtext">
          Insufficient visible arm frames in air
        </span>
      </div>
    ) : armMeasurement?.sustainedRatio != null ? (
      <>
        <div className="measurement-item">
          <span className="measurement-label">Arm Tightness (Sustained):</span>
          <span className="measurement-value">
            {((1 - armMeasurement.sustainedRatio) * 100).toFixed(0)}%
          </span>
          <span className="measurement-subtext">
            (Elite: 65%+)
          </span>
        </div>

        {armMeasurement.pullInTimeS != null && !isNaN(armMeasurement.pullInTimeS) && (
          <div className="measurement-item">
            <span className="measurement-label">Arm Pull-in Time:</span>
            <span className="measurement-value">
              {armMeasurement.pullInTimeS.toFixed(2)} s
            </span>
            <span className="measurement-subtext">
              Time to tightest arm position
            </span>
          </div>
        )}

        {armMeasurement.peakRatio != null && (
          <div className="measurement-item">
            <span className="measurement-label">Peak Arm Tightness:</span>
            <span className="measurement-value">
              {((1 - armMeasurement.peakRatio) * 100).toFixed(0)}%
            </span>
            <span className="measurement-subtext">
              Best single frame
            </span>
          </div>
        )}
      </>
    ) : armMeasurement?.peakRatio != null && (
      <div className="measurement-item">
        <span className="measurement-label">Peak Arm Tightness:</span>
        <span className="measurement-value">
          {((1 - armMeasurement.peakRatio) * 100).toFixed(0)}%
        </span>
        <span className="measurement-subtext">
          Best single frame
        </span>
      </div>
    )}

    {jumpMetrics.air_body_straightness_avg !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Body Straightness:</span>
        <span className="measurement-value">
          {(jumpMetrics.air_body_straightness_avg * 100).toFixed(0)}%
        </span>
        <span className="measurement-subtext">
          (best: {(jumpMetrics.air_body_straightness_best * 100).toFixed(0)}%)
        </span>
      </div>
    )}

    {/* Leg Tightness */}
    {legMeasurement && (
      <div className="measurement-item">
        <span className="measurement-label">
          {legMeasurement.climbTimeS != null && !isNaN(legMeasurement.climbTimeS)
            ? 'Leg Climb Time:'
            : 'Leg Tightness:'}
        </span>
        <span className="measurement-value">
          {legMeasurement.climbTimeS != null && !isNaN(legMeasurement.climbTimeS)
            ? `${legMeasurement.climbTimeS.toFixed(2)} s`
            : `${(legMeasurement.score * 100).toFixed(0)}%`}
        </span>
        <span className="measurement-subtext">
          {legMeasurement.climbTimeS != null && !isNaN(legMeasurement.climbTimeS)
            ? `Tightness: ${(legMeasurement.score * 100).toFixed(0)}%`
            : '(Elite: 65%+)'}
        </span>
      </div>
    )}

    {jumpMetrics.landing_speed_m_s !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Landing Speed:</span>
        <span className="measurement-value">
          {jumpMetrics.landing_speed_m_s.toFixed(2)} m/s
          ({(jumpMetrics.landing_speed_m_s * 3.6).toFixed(1)} km/h)
        </span>
      </div>
    )}

    {jumpMetrics.air_time !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Air Time:</span>
        <span className="measurement-value">
          {jumpMetrics.air_time.toFixed(3)} s
        </span>
        <span className="measurement-subtext">(Elite: 0.65s)</span>
      </div>
    )}

    {jumpMetrics.takeoff_velocity_peak_m_s !== undefined && (
      <div className="measurement-item">
        <span className="measurement-label">Takeoff Velocity:</span>
        <span className="measurement-value">
          {jumpMetrics.takeoff_velocity_peak_m_s.toFixed(2)} m/s
          ({(jumpMetrics.takeoff_velocity_peak_m_s * 3.6).toFixed(1)} km/h)
        </span>
      </div>
    )}

    {jumpMetrics.rotation_speed_quality && (
      <div className="measurement-item quality-badge">
        <span className="measurement-label">Quality:</span>
        <span className={`quality-level ${jumpMetrics.rotation_speed_quality}`}>
          {jumpMetrics.rotation_speed_quality.replace(/_/g, ' ')}
        </span>
      </div>
    )}
  </>
  );
};

export default KeyMeasurementsDisplay;
